
import { Component, Vue, Prop } from "vue-property-decorator";

import UserLogin from "@/models/User/UserLogin";
import {
  LOGIN_USER,
  TRANSLATIONS_FETCH,
} from "@/store/ActionTypes";
import { AuthStates } from "@/store/modules/AccountModule";
import { mapState } from "vuex";

import router from "@/router";
import CustomersRepository from "@/utils/API/CustomersRepository";
import vFooter from "@/components/vFooter.vue";
import { UserTypes } from "@/models/User/UserTypes";
import UserRepository from "@/utils/API/UserRepository";
import SubscriptionRepository from "@/utils/API/SubscriptionRepository";
import { Snotify } from "vue-snotify";
import SystemConfigurationRepository from "@/utils/API/SystemConfigurationRepository";
import { NutrientSourceList } from "@/models/NutrientSource/NutrientSourceList";
import SignupDto from "@/models/Restaurants/SignupDto";
import GeneralRepository from "@/utils/API/GeneralRepository";

@Component({
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountStoreState: (state: any) => state.AccountModule,
    }),
  },
  components: {
    vFooter,
  },
})
class Login extends Vue {
  @Prop() forgotPassword?: boolean;

  localizationState: any;
  accountStoreState: any;

  nutrientSources: NutrientSourceList[] = [];
  trialPeriodInDays: Number = 0;

  countries: any[] = [];
  industriesList: any[] = [];
  selectedCountry: any = null;
  selectedIndustry: any = null;

  user: UserLogin = { email: "", password: "", rememberMe: false };
  signupDto: SignupDto = {
    name: "",
    phone: "",
    ownerEmail: "",
    ownerPassword: "",
    confirmOwnerPassword: "",
    ownerFirstName: "",
    ownerLastName: "",
    referrer: null,
    countryId: null,
    industryId: null,
    hasRequestedNewsLetter: false,
    chosenNutrientSource: 0,
  };

  forgotPasswordEmail: string = "";
  invalidEmail: boolean = false;
  correctEmail: boolean = false;

  termsConditions: boolean = false;

  invalidPhone: boolean = false;
  phoneTouched: boolean = false;

  goToForgot() {
    this.invalidEmail = false;
    this.correctEmail = false;
    this.forgotPasswordEmail = "";

    this.$router.push("/forgot");
  }

  attemptResetPassword() {
    this.$validator.validateAll("resetPassword").then((result) => {
      if (result) {
        this.correctEmail = false;
        this.invalidEmail = false;
        UserRepository.requestResetPassword({ email: this.forgotPasswordEmail })
          .then(() => {
            this.correctEmail = true;
          })
          .catch(() => {
            this.invalidEmail = true;
          });
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  createAccount() {
    this.phoneTouched = true;
    this.$validator.validateAll("accCreate").then((result) => {
      if (result && !this.invalidPhone) {
        if (!this.termsConditions) {
          this.$snotify.error(
            this.translations.get("login_accept_terms_message")
          );
          return;
        }

        this.$snotify.async(
          this.translations.get("login_creating"),
          () => <Promise<Snotify>>new Promise((resolve, reject) => {
            this.signupDto.countryId = this.selectedCountry.id;
            this.signupDto.industryId = this.selectedIndustry.id;
            CustomersRepository.signup(this.signupDto)
              .then(() => {
                let page = process.env.VUE_APP_THANKYOUPAGE_URL;
                window.location = page;
              })
              .catch((error) => {
                let message = "";
                if (error.response) {
                  message =
                    error.response.data.errorMessages[0].userVisibleMessage;
                }
                else {
                  message = this.translations.get("network_error");
                }
                reject({ body: message });
              });
          }),
          {
            timeout: 3500,
            closeOnClick: true,
          }
        );
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  attemptLogin() {
    this.$validator.validateAll("login").then((result) => {
      if (result) {
        this.$snotify.async(
          this.translations.get("login_authenticating"),
          () => <Promise<Snotify>>new Promise((resolve, reject) => {
            this.$store
              .dispatch(LOGIN_USER, this.user)
              .then((response) => {
                let message: string = "";
                if (
                  [
                    UserTypes.Administrator,
                    UserTypes.SuperAdministrator,
                    UserTypes.Helpdesk,
                  ].includes(response.data.result.userType)
                ) {
                  message = "Welcome!";
                  router.push("/administration");
                }
                else {
                  message = `${this.translations.get(
                    "welcome",
                    this.$store.getters.getUserDetails.firstName
                  )}`;
                  router.push("/");
                }

                resolve({
                  body: message,
                });
              })
              .catch(() => {
                reject({
                  body: this.$store.getters.getAuthMessage,
                });
              });
          }),
          {
            timeout: 3500,
            closeOnClick: true,
          }
        );
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  handlePhoneValidation(response : any){
    this.invalidPhone = !response.isValid;
  }

  toggleCreateMode() {
    if (this.$route.name === "create") {
      this.$router.push({ path: "login" });
    }
    else {
      this.$router.push({ path: "create" });
    }
  }

  get isCreateMode() {
    return this.$route.name === "create";
  }

  get showMessage() {
    if (this.$store.getters.getAuthState === AuthStates.Failed) return true;
  }

  get translations() {
    return this.localizationState.translations;
  }

  nameOfNutrientSource(x: any) {
    if (x === 0) {
      return this.translations.get("nutrient_source_0");
    }
    if (x === 1) {
      return this.translations.get("nutrient_source_1");
    }
    if (x === 2) {
      return this.translations.get("nutrient_source_2");
    }
  }

  get transformedNutrientSources() {
    return this.nutrientSources
      .filter((x: NutrientSourceList) => x.isEnabled)
      .map((x: NutrientSourceList) => {
        return {
          name: x.nutrientSource,
          value: x.nutrientSource,
        };
      });
  }

  fetchNutrientSources() {
    SystemConfigurationRepository.fetchNutrientSourcesList()
      .then((response) => {
        this.nutrientSources = response.data.result;
      })
      .catch((error) => {
        if (
          error.response &&
          error.response.errorMessages &&
          error.response.errorMessages.length > 0 &&
          error.response.errorMessages[0].userVisibleMessage
        )
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
        else {
          this.$snotify.error(this.translations.get("network-error"));
        }
      });
  }

  created() {
    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: [
        "common",
        "login",
        "customers",
        "managesubscription",
        "profile",
      ],
    });
    document.title = "Sign In or Create Account | MenuSano";
    this.fetchNutrientSources();

    SubscriptionRepository.getSubscriptionSystemSettings()
      .then((response) => {
        this.trialPeriodInDays = response.data.result.trialDays;
      })
      .catch((error) => {
        if (
          error.response &&
          error.response.errorMessages &&
          error.response.errorMessages.length > 0 &&
          error.response.errorMessages[0].userVisibleMessage
        )
          this.$snotify.error(
            error.response.errorMessages[0].userVisibleMessage
          );
        else {
          this.$snotify.error(this.translations.get("network-error"));
          this.trialPeriodInDays = 0;
        }
      });
    GeneralRepository.listCountries()
      .then((response) => {
        this.countries = response.data.result;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.statusText
            //error.response.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });

    GeneralRepository.listIndustries()
      .then((response) => {
        this.industriesList = response.data.result;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.statusText
            //error.response.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }
}
export default Login;
